import type { ICDateData, IOrder, IReservation } from '@curbnturf/entities';
import { CDate, HelperFunctions, PriceDisplay, Status } from '@curbnturf/entities';
import type { BookingStatus, IBooking, IBookingAddOn } from '@cnt-workspace/models';
import { addDaysIso, parseIsoLocal, priorDayIso } from '../shared/iso-date.util';

/**
 * Maps the real, relational `IReservation` (site → listing, priceComponents
 * array, activity log) onto cnt-landing's flat `IBooking` "receipt" view
 * model, so every existing read-only consumer (trips, dashboard, inbox,
 * notifications, availability) keeps working unchanged against
 * `BookingService.bookings$` regardless of whether a booking is real or mock.
 *
 * Known lossy mappings (the real model is coarser or differently-shaped than
 * the mock view):
 *  - `guests`: no real backend field exists anywhere on IReservation/IOrder.
 *    Falls back to `site.maxGuests` (a capacity cap, not the guest's actual
 *    chosen count) since there's nothing truer to read.
 *  - `cleaningFee`: not a distinct line-item concept server-side — always 0
 *    for real bookings; would show as an `extras` row if a listing has one.
 *  - `hostAddress`: not modeled on IListing at all.
 *  - `addOns`: real `IReservationExtra` has no `unit`/`icon`/`photo` concept
 *    (see draft-listing-real-mapper.ts's own lossy-mapping note on
 *    `IListingExtra`) — reconstructed here as flat `'per stay'` lines, since
 *    the real backend has no per-night/per-person/per-unit multiplier either
 *    (`count` is the only quantity concept, already applied server-side).
 */

const STATUS_MAP: Record<string, BookingStatus> = {
  [Status.requested]: 'pending',
  [Status.changeRequested]: 'pending',
  [Status.processing]: 'pending',
  [Status.accepted]: 'approved',
  [Status.paid]: 'confirmed',
  [Status.completed]: 'confirmed',
  [Status.rated]: 'confirmed',
  [Status.rejected]: 'declined',
  [Status.cancelled]: 'cancelled',
};

function reservationStatusToBookingStatus(reservation: IReservation): BookingStatus {
  const raw = reservation.status || reservation.activity?.[reservation.activity.length - 1]?.status || '';
  return STATUS_MAP[raw as string] ?? 'pending';
}

/** Real `IStatusUpdate.reason` off the latest activity entry — the real
 *  analog of cnt-landing's `cancelReason`/decline reason fields. */
function latestReason(reservation: IReservation): string | undefined {
  const activity = reservation.activity;
  if (!activity || activity.length === 0) return undefined;
  return activity[activity.length - 1].reason || undefined;
}

/** Epoch-seconds `IStatusUpdate.timestamp` → ISO string. */
function activityIso(reservation: IReservation, predicate: (status: string) => boolean): string | undefined {
  const activity = reservation.activity;
  if (!activity) return undefined;
  const hit = [...activity].reverse().find(a => predicate(a.status as string));
  return hit ? new Date(hit.timestamp * 1000).toISOString() : undefined;
}

/** [checkIn ISO date, checkOut ISO date) — every reservation response passes
 *  through the server's `ReservationUserCleaningInterceptor`, which calls
 *  `Reservation.changeLastNightToEndDate()`, so `to` already arrives as the
 *  exclusive checkout date (storage keeps it as the last night). `to` is only
 *  absent if a response ever skips that interceptor — treat that as one night. */
function datesFromCDate(date?: ICDateData): { start: string; end: string; nights: number } {
  if (!date?.from) return { start: '', end: '', nights: 0 };
  const start = date.from;
  const end = date.to && date.to > start ? date.to : addDaysIso(start, 1);
  const startDay = parseIsoLocal(start);
  const endDay = parseIsoLocal(end);
  const nights = startDay && endDay
    ? Math.max(1, Math.round((endDay.getTime() - startDay.getTime()) / 86_400_000))
    : 1;
  return { start, end, nights };
}

function hostNameFrom(reservation: IReservation): string {
  const user = reservation.site?.listing?.user;
  const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  return name || 'Host';
}

function listingLocationFrom(reservation: IReservation): string {
  const address = reservation.site?.listing?.address;
  if (!address?.city) return '';
  return address.state ? `${address.city}, ${address.state}` : address.city;
}

function rvSummaryFrom(reservation: IReservation): string {
  const rv = reservation.rv;
  if (!rv) return '';
  const label = [rv.year, rv.make, rv.model].filter(Boolean).join(' ') || rv.type || 'RV';
  return rv.length ? `${label} · ${rv.length} ft` : label;
}

/** Best-effort `IBooking.contact` — real reservations don't store a
 *  separate booking-time contact snapshot; the guest's own account email is
 *  the closest real analog. */
function contactFrom(reservation: IReservation): { email: string; phone?: string } {
  return { email: reservation.user?.email || '', phone: reservation.user?.phone };
}

/** `IReservation.extras` → `IBookingAddOn[]`, for receipts (trips/dashboard/inbox). */
function addOnsFrom(reservation: IReservation): IBookingAddOn[] | undefined {
  // Extras for a change still awaiting the host sit alongside the originals.
  const extras = reservation.extras?.filter((e) => e.pendingChange !== true);
  if (!extras || extras.length === 0) return undefined;
  return extras.map((e) => {
    const price = e.extra?.price ?? 0;
    const count = e.count || 1;
    return {
      id: String(e.extra?.id ?? e.extraId ?? e.id ?? ''),
      label: e.extra?.label || e.extra?.name || 'Add-on',
      unit: 'per stay',
      unitPrice: price,
      quantity: count,
      amount: roundMoney(price * count),
      realExtraId: e.extra?.id ?? e.extraId,
    };
  });
}

/** Money to cents — PriceDisplay sums raw floats in places (e.g. `nights`). */
export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Guest-facing totals off a set of price rows. */
export function priceSummaryFrom(prices: PriceDisplay | null): {
  nightsSubtotal: number;
  extrasTotal: number;
  discounts: { label: string; amount: number }[];
  taxTotal: number;
  total: number;
} {
  if (!prices) return { nightsSubtotal: 0, extrasTotal: 0, discounts: [], taxTotal: 0, total: 0 };
  return {
    nightsSubtotal: roundMoney((prices.nights ?? []).reduce((sum, n) => sum + n.value, 0)),
    extrasTotal: roundMoney((prices.extras ?? []).reduce((sum, e) => sum + e.value, 0)),
    // PriceDisplay stores discounts as negative line values.
    discounts: (prices.discounts ?? [])
      .filter((d) => d.value !== 0)
      .map((d) => ({ label: d.key, amount: roundMoney(Math.abs(d.value)) })),
    taxTotal: roundMoney(prices.tax ?? 0),
    total: roundMoney(prices.guestGross ?? 0),
  };
}

/** `pendingDate` skips the response interceptor that turns `date.to` into the
 *  checkout day, so it still holds the last night (inclusive). */
function pendingChangeFrom(reservation: IReservation): IBooking['pendingChange'] {
  const pendingRows = (reservation.priceComponents ?? []).filter((p) => p.pendingChange === true);
  const pendingDate = reservation.pendingDate;
  if (!pendingDate?.from && pendingRows.length === 0) return undefined;
  const total = priceSummaryFrom(pendingRows.length > 0 ? new PriceDisplay(pendingRows) : null).total;
  if (!pendingDate?.from) {
    // Change requested before pending dates were stored: the reservation row
    // already carries the requested dates.
    return { ...datesFromCDate(reservation.date), total };
  }
  const lastNight = pendingDate.to || pendingDate.from;
  return { ...datesFromCDate({ from: pendingDate.from, to: addDaysIso(lastNight, 1) } as ICDateData), total };
}

export function toBookingView(reservation: IReservation): IBooking {
  const status = reservationStatusToBookingStatus(reservation);
  const changeRequested = reservation.status === Status.changeRequested;
  const { start, end, nights } = datesFromCDate(reservation.date);
  const priceComponents = reservation.priceComponents ?? [];
  // While a change waits on the host, the booking still stands at its original
  // price — show those rows; the requested ones surface via `pendingChange`.
  const prices = priceComponents.length > 0 ? new PriceDisplay(priceComponents, true) : null;
  const summary = priceSummaryFrom(prices);
  const subtotal = summary.nightsSubtotal;
  const photo = reservation.site?.listing?.photos?.[0] ?? reservation.site?.photos?.[0];
  const addOns = addOnsFrom(reservation);
  const addOnsTotal = addOns ? roundMoney(addOns.reduce((sum, a) => sum + a.amount, 0)) : undefined;

  return {
    id: `res-${reservation.id}`,
    userEmail: reservation.user?.email || '',
    listingId: reservation.site?.listing?.id ?? 0,
    listingTitle: reservation.site?.listing?.name || reservation.site?.name || 'Listing',
    listingLocation: listingLocationFrom(reservation),
    listingPhoto: photo ? HelperFunctions.getPhotoUrlFullSize(photo) : '',
    hostName: hostNameFrom(reservation),
    lat: reservation.site?.coordinates?.lat,
    lng: reservation.site?.coordinates?.lon,
    dates: { start, end },
    nights,
    guests: reservation.site?.maxGuests ?? 1,
    rvSummary: rvSummaryFrom(reservation),
    pricePerNight: prices?.nightlyAverage ?? 0,
    subtotal,
    addOns,
    addOnsTotal,
    cleaningFee: 0,
    serviceFee: prices?.fees ?? 0,
    feedbackIncentive: prices?.feedbackIncentive,
    total: summary.total,
    discounts: summary.discounts.length > 0 ? summary.discounts : undefined,
    taxTotal: summary.taxTotal || undefined,
    instantBook: reservation.site?.listing?.reservationProcess === 'instant',
    status,
    createdAt: activityIso(reservation, s => s === Status.requested || s === Status.accepted)
      ?? new Date().toISOString(),
    contact: contactFrom(reservation),
    cancelReason: status === 'cancelled' ? latestReason(reservation) : undefined,
    reviewedAt: reservation.listingRated ? activityIso(reservation, s => s === Status.rated) : undefined,
    hostReviewedAt: reservation.guestRated ? activityIso(reservation, s => s === Status.rated) : undefined,
    realReservationId: reservation.id,
    realSiteId: reservation.site?.id,
    guestUserId: reservation.user?.id,
    hostUserId: reservation.site?.listing?.user?.id,
    realRvId: reservation.rvId ?? reservation.rv?.id,
    cancellationPolicy: reservation.site?.listing?.cancellationPolicy as string | undefined,
    changeRequested: changeRequested || undefined,
    pendingChange: changeRequested ? pendingChangeFrom(reservation) : undefined,
  };
}

/** Converts an [checkIn, exclusive checkOut) ISO date pair (cnt-landing's
 *  `IBooking.dates` convention) into the `CDate` the server stores
 *  (`to` = last night, inclusive; undefined for one night). Note this is NOT
 *  the shape responses come back in — see `datesFromCDate`. Real
 *  `IReservation`/`IOrder` fields require the class (not just the
 *  `{from, to}` data shape) since `ICDate` declares methods. */
export function isoRangeToCDate(startIso: string, endIso: string): CDate {
  const start = startIso.slice(0, 10);
  const lastNight = priorDayIso(endIso.slice(0, 10));
  return new CDate({ from: start, to: lastNight === start ? undefined : lastNight });
}

/** Selected add-ons → `IReservation.extras`, keyed by the real
 *  `IListingExtra.id` (`IBookingAddOn.realExtraId`) — add-ons never synced to
 *  the real backend (no `realExtraId`, e.g. host added them only to a mock
 *  listing) are dropped rather than sent with a garbage id. */
export function addOnsToReservationExtras(addOns: IBookingAddOn[] | undefined): IReservation['extras'] {
  return (addOns ?? [])
    .filter((a): a is IBookingAddOn & { realExtraId: number } => typeof a.realExtraId === 'number')
    .map((a) => ({ count: a.quantity, extra: { id: a.realExtraId } }));
}

/** Selected add-ons → `IOrder.extras` (flat `{id, count}`, unlike
 *  `IReservation.extras`'s nested `{extra: {id}}`) — for `PUT
 *  /reservation/:id/change`. Same real-id-only filtering as
 *  `addOnsToReservationExtras`. */
export function addOnsToOrderExtras(addOns: IBookingAddOn[] | undefined): IOrder['extras'] {
  return (addOns ?? [])
    .filter((a): a is IBookingAddOn & { realExtraId: number } => typeof a.realExtraId === 'number')
    .map((a) => ({ id: a.realExtraId, count: a.quantity }));
}

/** Build the create payload for `POST /reservation` — mirrors curbnturf's
 *  own `OrderFacade.toReservation()`: only date/site/extras/rv go in the
 *  request body, `user` is set server-side from the auth token, and
 *  `priceComponents` are computed server-side (never client-supplied). */
export function buildReservationCreatePayload(input: {
  siteId: number;
  startIso: string;
  /** Exclusive checkout date (matches IBooking.dates.end convention). */
  endIso: string;
  rvId?: number;
  addOns?: IBookingAddOn[];
}): IReservation {
  return {
    date: isoRangeToCDate(input.startIso, input.endIso),
    site: { id: input.siteId },
    extras: addOnsToReservationExtras(input.addOns),
    rv: input.rvId ? { id: input.rvId } : undefined,
  };
}
